// @feature-spec brain/vidmax/feature-specs/admin-ad-engine-review.md
/**
 * `/admin/ad-engine/review` — human review of the takes QA was unsure about (VM-1179).
 *
 * The server decides what is flagged (`reviewOf` → `needs_review`); this page only shows those,
 * across every ad, and records P/F answers on `take.qa.human` (calibration ground truth — never a
 * selection). Static segment beside `ad-engine.index.tsx`, same reasoning as `ad-engine.formats.tsx`.
 */
import type { IAdReviewItem, IAdReviewProject } from "@aix/vidmax-shared/src/schemas/adProject"
import { EAdHumanVerdict, EAdReviewFilter, EAdStage } from "@aix/vidmax-shared/src/schemas/adProject"
import { AixContentHeader, Button, toast } from "@aix/ui"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"

import { ReviewQueue } from "~/components/AdEngine/ReviewQueue"
import { neighbourKey, reviewItemKey, reviewItemKeys } from "~/components/AdEngine/reviewQueueView"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"
import { api } from "~/utils/api"

type TReviewStage = EAdStage.KEYFRAME | EAdStage.GENERATE

const REVIEW_STAGES: TReviewStage[] = [EAdStage.KEYFRAME, EAdStage.GENERATE]

/** Typing a note must not answer the take. */
const isTypingTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))

function AdEngineReviewPage() {
  const search = Route.useSearch()
  const stage = search.stage ?? EAdStage.KEYFRAME
  const view = search.view ?? EAdReviewFilter.OPEN
  const routeNavigate = Route.useNavigate()
  const answered = view === EAdReviewFilter.ANSWERED
  const utils = api.useUtils()

  const queueQuery = api.adEngine.reviewQueue.useQuery({ stage, filter: view })
  const projects = useMemo(() => queueQuery.data?.projects ?? [], [queueQuery.data])
  const keys = useMemo(() => reviewItemKeys(projects), [projects])

  const [focusedKey, setFocusedKey] = useState<string>()
  const [note, setNote] = useState("")
  // A refetch can drop the focused item (answered elsewhere, re-graded) — fall back to the first.
  const activeKey = focusedKey && keys.includes(focusedKey) ? focusedKey : keys[0]

  const focus = useCallback((key: string | undefined) => {
    setFocusedKey(key)
    setNote("")
    if (key)
      document.querySelector(`[data-review-key="${key}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [])

  const record = api.adEngine.recordReview.useMutation({
    onSuccess: async () => {
      await utils.adEngine.reviewQueue.invalidate()
    },
    onError: (error) => toast.error(error.message),
  })

  const answer = useCallback(
    (project: IAdReviewProject, item: IAdReviewItem, verdict: EAdHumanVerdict) => {
      const key = reviewItemKey(project.videoId, item)
      // Open view: the answered item leaves the list, so move on now. Answered view: stay put.
      if (!answered) focus(neighbourKey(keys, key))
      record.mutate({
        videoId: project.videoId,
        stage: item.stage,
        unitId: item.unitId,
        jobId: item.jobId,
        verdict,
        ...(note.trim() ? { note: note.trim() } : {}),
      })
    },
    [answered, focus, keys, note, record],
  )

  const findItem = useCallback(
    (key: string | undefined) => {
      for (const project of projects) {
        const item = project.items.find((candidate) => reviewItemKey(project.videoId, candidate) === key)
        if (item) return { project, item }
      }
      return undefined
    },
    [projects],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return
      const index = activeKey ? keys.indexOf(activeKey) : -1
      const key = event.key.toLowerCase()
      if (key === "j" || key === "arrowdown" || key === "s") {
        event.preventDefault()
        focus(keys[Math.min(index + 1, keys.length - 1)])
      } else if (key === "k" || key === "arrowup") {
        event.preventDefault()
        focus(keys[Math.max(index - 1, 0)])
      } else if ((key === "p" || key === "f") && !record.isLoading) {
        const found = findItem(activeKey)
        if (!found) return
        event.preventDefault()
        answer(found.project, found.item, key === "p" ? EAdHumanVerdict.PASS : EAdHumanVerdict.FAIL)
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [activeKey, answer, findItem, focus, keys, record.isLoading])

  const pendingKey =
    record.isLoading && record.variables ? `${record.variables.videoId}:${record.variables.jobId}` : undefined
  const setSearch = (patch: { stage?: TReviewStage; view?: EAdReviewFilter }) =>
    void routeNavigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  return (
    <SuperAdminLayout>
      <CustomHead title="Ad Engine review" />
      <div className="container mx-auto space-y-6 pb-12">
        <AixContentHeader className="px-8 pt-8">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex items-baseline gap-3">
              <h1 className="text-2xl font-bold">Ad Engine review</h1>
              <span className="text-muted-foreground text-sm">
                {queueQuery.data
                  ? `${String(queueQuery.data.total)} ${answered ? "answered" : "to review"} · newest ${String(queueQuery.data.scannedProjects)} ads scanned`
                  : ""}
              </span>
            </div>
            <Button size="sm" variant="outline" asChild>
              <Link to="/admin/ad-engine">← All ads</Link>
            </Button>
          </div>
        </AixContentHeader>
        <div className="space-y-4 px-8">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={answered ? "outline" : "default"}
              onClick={() => setSearch({ view: EAdReviewFilter.OPEN })}
            >
              Needs review
            </Button>
            <Button
              size="sm"
              variant={answered ? "default" : "outline"}
              onClick={() => setSearch({ view: EAdReviewFilter.ANSWERED })}
            >
              Answered
            </Button>
            <span className="bg-border mx-2 h-5 w-px" />
            {REVIEW_STAGES.map((candidate) => (
              <Button
                key={candidate}
                size="sm"
                variant={candidate === stage ? "secondary" : "ghost"}
                onClick={() => setSearch({ stage: candidate })}
              >
                {candidate === EAdStage.KEYFRAME ? "Keyframes" : "Clips"}
              </Button>
            ))}
            <span className="text-muted-foreground ml-auto text-xs">
              <kbd>J</kbd>/<kbd>K</kbd> move · <kbd>P</kbd> pass · <kbd>F</kbd> fail · <kbd>S</kbd> skip
            </span>
          </div>
          <ReviewQueue
            projects={projects}
            isLoading={queueQuery.isLoading}
            answered={answered}
            focusedKey={activeKey}
            pendingKey={pendingKey}
            note={note}
            onNoteChange={setNote}
            onFocus={(key) => {
              if (key !== activeKey) focus(key)
            }}
            onAnswer={answer}
            onSkip={() => focus(keys[Math.min((activeKey ? keys.indexOf(activeKey) : -1) + 1, keys.length - 1)])}
          />
        </div>
      </div>
    </SuperAdminLayout>
  )
}

/** Both optional so a bare `<Link to="/admin/ad-engine/review">` works; defaults applied on read. */
interface IAdEngineReviewSearch {
  stage?: TReviewStage
  view?: EAdReviewFilter
}

export const Route = createFileRoute("/admin/ad-engine/review")({
  component: AdEngineReviewPage,
  validateSearch: (search: Record<string, unknown>): IAdEngineReviewSearch => {
    const stage = REVIEW_STAGES.find((candidate) => candidate === search.stage)
    return {
      ...(stage ? { stage } : {}),
      ...(search.view === EAdReviewFilter.ANSWERED ? { view: EAdReviewFilter.ANSWERED } : {}),
    }
  },
})
